import { getRosaryConfig } from '../constants/rosarySequences';
import { PrayerType } from '../types';

export interface PrayerStepMapping {
  stepIndex: number;
  beadIndex: number;
  stepId: string;
  beadId: string;
  description: string;
}

// Decade bead ranges for the rosary
// Each decade starts with an Our Father bead and has 10 Hail Mary beads
// Note: Bead 5 now handles Glory Be, Mystery announcement, AND 1st decade Our Father
export interface DecadeBeadRange {
  decade: number;
  ourFatherBead: number;
  firstHailMaryBead: number;
  lastHailMaryBead: number;
}

export const DECADE_BEAD_RANGES: DecadeBeadRange[] = [
  { decade: 1, ourFatherBead: 5, firstHailMaryBead: 6, lastHailMaryBead: 15 },
  { decade: 2, ourFatherBead: 16, firstHailMaryBead: 17, lastHailMaryBead: 26 },
  { decade: 3, ourFatherBead: 27, firstHailMaryBead: 28, lastHailMaryBead: 37 },
  { decade: 4, ourFatherBead: 38, firstHailMaryBead: 39, lastHailMaryBead: 48 },
  { decade: 5, ourFatherBead: 49, firstHailMaryBead: 50, lastHailMaryBead: 59 },
];

// Get which decade a bead belongs to (returns null if not in a decade)
export const getDecadeForBead = (beadIndex: number): DecadeBeadRange | null => {
  for (const range of DECADE_BEAD_RANGES) {
    if (beadIndex >= range.ourFatherBead && beadIndex <= range.lastHailMaryBead) {
      return range;
    }
  }
  return null;
};

// Our Father bead phase - represents which set of prayers you're on
// 'closing' = Glory Be, Fatima Prayer, (54-day novena prayer) from previous decade
// 'opening' = Announce mystery, Our Father for current decade
export type OurFatherPhase = 'closing' | 'opening';

// Extended position info that includes phase for Our Father beads
export interface BeadPosition {
  decade: number;
  position: number; // 0 = Our Father, 1-10 = Hail Marys
  isOurFather: boolean;
  hailMaryNumber?: number;
  ourFatherPhase?: OurFatherPhase; // Only set when isOurFather is true
}

// Get the position within a decade (0 = Our Father, 1-10 = Hail Marys)
// Does NOT include phase - use getFullBeadPosition for that
export const getPositionInDecade = (beadIndex: number): BeadPosition | null => {
  const decadeRange = getDecadeForBead(beadIndex);
  if (!decadeRange) return null;

  if (beadIndex === decadeRange.ourFatherBead) {
    return { decade: decadeRange.decade, position: 0, isOurFather: true };
  }

  const hailMaryNumber = beadIndex - decadeRange.firstHailMaryBead + 1;
  return {
    decade: decadeRange.decade,
    position: hailMaryNumber,
    isOurFather: false,
    hailMaryNumber
  };
};

// Check if a bead is the Our Father bead for a specific decade
export const isOurFatherBead = (beadIndex: number): boolean => {
  return DECADE_BEAD_RANGES.some(range => range.ourFatherBead === beadIndex);
};

// Get which decade's Our Father bead this is (1-5), or null if not an Our Father bead
export const getOurFatherDecade = (beadIndex: number): number | null => {
  const range = DECADE_BEAD_RANGES.find(r => r.ourFatherBead === beadIndex);
  return range ? range.decade : null;
};

// Create detailed mapping for daily rosary/54-day novena
export const createRosaryStepMapping = (prayerType: PrayerType): PrayerStepMapping[] => {
  const mappings: PrayerStepMapping[] = [];

  if (prayerType === 'daily-rosary' || prayerType === '54-day-novena') {

    // For daily-rosary, step 0 is 'set-intention', step 1 is 'intro'
    // For 54-day-novena, step 0 is 'intro', step 1 is 'opening-prayer'
    const hasIntentionStep = prayerType === 'daily-rosary';

    // Intention step (daily rosary only) - no bead
    if (hasIntentionStep) {
      mappings.push({
        stepIndex: 0,
        beadIndex: 0,
        stepId: 'set-intention',
        beadId: 'cross',
        description: 'Set Your Intention'
      });
    }

    // Intro step -> Cross (bead 0)
    const introStepIndex = hasIntentionStep ? 1 : 0;
    mappings.push({
      stepIndex: introStepIndex,
      beadIndex: 0,
      stepId: 'intro',
      beadId: 'cross',
      description: 'Sign of the Cross, Introduction'
    });

    // 54-day novena has opening prayer to Mary
    if (prayerType === '54-day-novena') {
      mappings.push({
        stepIndex: 1,
        beadIndex: 0,
        stepId: 'opening-prayer',
        beadId: 'cross',
        description: 'Opening Prayer to Mary'
      });
    }

    // Rosary opening prayers -> Cross (bead 0) for Apostle's Creed
    // User stays on cross for the Apostle's Creed, then swipes to move to bead 1 for Our Father
    // This step covers: Apostles' Creed (cross), Our Father (bead 1), 3 Hail Marys (beads 2-4), Glory Be (bead 5)
    const openingStepIndex = prayerType === '54-day-novena' ? 2 : (hasIntentionStep ? 2 : 1);
    mappings.push({
      stepIndex: openingStepIndex,
      beadIndex: 0, // Stay on cross for Apostle's Creed
      stepId: 'rosary-opening',
      beadId: 'cross',
      description: 'Apostles Creed, Our Father, 3 Hail Marys, Glory Be'
    });

    // Mysteries introduction -> Bead 5 (consolidates Glory Be, Mystery announcement, 1st decade Our Father)
    mappings.push({
      stepIndex: openingStepIndex + 1,
      beadIndex: 5,
      stepId: 'mysteries-intro',
      beadId: 'glory-be-intro',
      description: 'Mysteries Introduction'
    });

    // Five decades -> Each starts at the Our Father bead
    // Note: Decade 1 Our Father is also on bead 5 (consolidated with mysteries intro)
    for (let decade = 0; decade < 5; decade++) {
      const stepIndex = openingStepIndex + 2 + decade;
      const decadeRange = DECADE_BEAD_RANGES[decade];

      mappings.push({
        stepIndex,
        beadIndex: decadeRange.ourFatherBead,
        stepId: `decade-${decade + 1}`,
        beadId: `decade-${decade + 1}-our-father`,
        description: `${decade + 1}${decade === 0 ? 'st' : decade === 1 ? 'nd' : decade === 2 ? 'rd' : 'th'} Mystery`
      });
    }

    // Final prayers -> Final medal (bead 60)
    const finalStepStart = openingStepIndex + 7;
    mappings.push({
      stepIndex: finalStepStart,
      beadIndex: 60,
      stepId: 'hail-holy-queen',
      beadId: 'final-prayers',
      description: 'Hail Holy Queen'
    });

    mappings.push({
      stepIndex: finalStepStart + 1,
      beadIndex: 60,
      stepId: 'rosary-prayer',
      beadId: 'final-prayers',
      description: 'Rosary Prayer'
    });

    if (prayerType === '54-day-novena') {
      mappings.push({
        stepIndex: finalStepStart + 2,
        beadIndex: 60,
        stepId: 'closing',
        beadId: 'final-prayers',
        description: 'Closing Prayer'
      });

      mappings.push({
        stepIndex: finalStepStart + 3,
        beadIndex: 60,
        stepId: 'prayer-reflection',
        beadId: 'final-prayers',
        description: 'Prayer Reflection'
      });

      mappings.push({
        stepIndex: finalStepStart + 4,
        beadIndex: 60,
        stepId: 'complete',
        beadId: 'final-prayers',
        description: 'Prayer Complete'
      });
    } else {
      mappings.push({
        stepIndex: finalStepStart + 2,
        beadIndex: 60,
        stepId: 'prayer-reflection',
        beadId: 'final-prayers',
        description: 'Prayer Reflection'
      });

      mappings.push({
        stepIndex: finalStepStart + 3,
        beadIndex: 60,
        stepId: 'complete',
        beadId: 'final-prayers',
        description: 'Prayer Complete'
      });
    }
  }

  return mappings;
};

// Create mapping for chaplets
export const createChapletStepMapping = (chapletType: string): PrayerStepMapping[] => {
  const mappings: PrayerStepMapping[] = [];
  const config = getRosaryConfig(chapletType);
  
  // For chaplets, create a simpler linear mapping
  // Each major prayer step corresponds to a key bead
  
  if (chapletType === 'divine-mercy') {
    // PrayerModal generates these steps for Divine Mercy (in order):
    //   0: set-intention, 1: chaplet-intro, 2: sign-of-cross,
    //   3: our-father-hail-mary-creed, 4..8: decades 1..5,
    //   9: closing-prayer, 10: prayer-reflection, 11: chaplet-complete.
    // Steps 0, 10, and 11 are intentionally unmapped — they fall back to
    // bead 0 / bead 61 via getBeadIndexForStep's "nearest preceding" rule.
    mappings.push({
      stepIndex: 1,
      beadIndex: 0,
      stepId: 'chaplet-intro',
      beadId: 'cross',
      description: 'Chaplet Introduction'
    });
    mappings.push({
      stepIndex: 2,
      beadIndex: 0,
      stepId: 'sign-of-cross',
      beadId: 'cross',
      description: 'Sign of the Cross, optional Opening Prayer'
    });
    mappings.push({
      stepIndex: 3,
      beadIndex: 1,
      stepId: 'our-father-hail-mary-creed',
      beadId: 'our-father-1',
      description: 'Our Father, 3 Hail Marys, Apostles\' Creed'
    });

    // Five decades — each anchored at its Eternal Father (large) bead.
    // Decade 1 sits at bead 2 (directly after the opening large bead); each
    // following decade is 11 beads later (1 large + 10 small).
    const decadeStepIds = ['first-decade', 'second-decade', 'third-decade', 'fourth-decade', 'fifth-decade'];
    for (let decade = 0; decade < 5; decade++) {
      mappings.push({
        stepIndex: 4 + decade,
        beadIndex: 2 + (decade * 11),
        stepId: decadeStepIds[decade],
        beadId: `decade-${decade + 1}-eternal-father`,
        description: `Decade ${decade + 1}: Eternal Father + 10 "For the sake of His sorrowful Passion"`
      });
    }

    mappings.push({
      stepIndex: 9,
      beadIndex: 57,
      stepId: 'closing-prayer',
      beadId: 'closing-trisagion',
      description: 'Holy God, Holy Mighty One, Holy Immortal One (x3)'
    });
  } else {
    // Generic chaplet mapping
    const totalSteps = Math.min(config.beadSequence.length / 5, 10); // Estimate steps
    
    for (let i = 0; i < totalSteps; i++) {
      const beadIndex = Math.floor((i / totalSteps) * config.beadSequence.length);
      mappings.push({
        stepIndex: i,
        beadIndex,
        stepId: `step-${i}`,
        beadId: config.beadSequence[beadIndex]?.id || `bead-${beadIndex}`,
        description: `Prayer Step ${i + 1}`
      });
    }
  }
  
  return mappings;
};

// Main function to get step mapping for any prayer type
export const getPrayerStepMapping = (prayerType: PrayerType, chapletType?: string): PrayerStepMapping[] => {
  if (prayerType === 'daily-rosary' || prayerType === '54-day-novena') {
    return createRosaryStepMapping(prayerType);
  } else if (prayerType === 'chaplet' && chapletType) {
    return createChapletStepMapping(chapletType);
  }
  
  return [];
};

// Helper functions for the PrayerModal
export const getBeadIndexForStep = (stepIndex: number, prayerType: PrayerType, chapletType?: string): number => {
  const mappings = getPrayerStepMapping(prayerType, chapletType);
  
  // Find exact match first
  const exactMatch = mappings.find(mapping => mapping.stepIndex === stepIndex);
  if (exactMatch) {
    return exactMatch.beadIndex;
  }
  
  // Find closest match (step just before current step)
  const previousMappings = mappings.filter(mapping => mapping.stepIndex <= stepIndex);
  if (previousMappings.length > 0) {
    return previousMappings[previousMappings.length - 1].beadIndex;
  }
  
  // Default to first bead
  return 0;
};

export const getStepIndexForBead = (beadIndex: number, prayerType: PrayerType, chapletType?: string): number => {
  const mappings = getPrayerStepMapping(prayerType, chapletType);
  
  // Find exact match first
  const exactMatch = mappings.find(mapping => mapping.beadIndex === beadIndex);
  if (exactMatch) {
    return exactMatch.stepIndex;
  }
  
  // Find closest match (bead just before current bead)
  const previousMappings = mappings.filter(mapping => mapping.beadIndex <= beadIndex);
  if (previousMappings.length > 0) {
    return previousMappings[previousMappings.length - 1].stepIndex;
  }
  
  // Default to first step
  return 0;
};